// A mine: a timber-framed pit-head (headframe + winch) over a dark adit cut into
// a stone mound, with a loaded ore cart out front. Ported 1:1 from age-of-mcp's
// mine.ts: the shared foundation/scaffold come from the library, the stone mound
// is a local helper, and the four build stages are transcribed from stage(s).
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, spin, EFFECT_LOOP, type Part } from "../../lib";

const ROCK = "#6a6660"; // mountain stone
const TIMBER = "#6e4d2c"; // pit-head timber
const DARK = "#161313"; // the dark adit mouth
const ORE = "#9a8a6a"; // ore chunks
const CART = "#4a4036"; // mine cart

// A stone mound the mine is sunk into, `h` tiles tall.
function mound(h: number): Part {
  return { geometry: new THREE.BoxGeometry(0.7, h, 0.7), color: ROCK, position: [0, 0.14 + h / 2, 0] };
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), mound(0.3), ...scaffold(0.82, 0.6)];
  if (s === 2) return [foundationSlab(0.84), mound(0.55), ...scaffold(0.82, 1.0)];

  const h = 0.62, top = 0.14 + h;
  return [
    foundationSlab(0.84),
    mound(h),
    // The dark adit mouth on the front, with a timber lintel.
    { geometry: new THREE.BoxGeometry(0.3, 0.34, 0.08), color: DARK, position: [0, 0.14 + 0.18, 0.34] },
    { geometry: new THREE.BoxGeometry(0.4, 0.06, 0.1), color: TIMBER, position: [0, 0.14 + 0.37, 0.34] },
    { geometry: new THREE.BoxGeometry(0.06, 0.36, 0.08), color: TIMBER, position: [-0.17, 0.14 + 0.18, 0.34] },
    { geometry: new THREE.BoxGeometry(0.06, 0.36, 0.08), color: TIMBER, position: [0.17, 0.14 + 0.18, 0.34] },
    // A timber headframe (A-frame winch tower) over the shaft.
    { geometry: new THREE.BoxGeometry(0.05, 0.5, 0.05), color: TIMBER, position: [-0.12, top + 0.25, -0.05], rotation: [0, 0, 0.18] },
    { geometry: new THREE.BoxGeometry(0.05, 0.5, 0.05), color: TIMBER, position: [0.12, top + 0.25, -0.05], rotation: [0, 0, -0.18] },
    { geometry: new THREE.BoxGeometry(0.32, 0.06, 0.08), color: TIMBER, position: [0, top + 0.48, -0.05] },
    // The winch axle plus a crank peg riding it — named so the "active"
    // animation can turn them together about the axle.
    { geometry: new THREE.CylinderGeometry(0.04, 0.04, 0.34, 6), color: "#3a2c1c", rotation: [0, 0, Math.PI / 2], position: [0, top + 0.4, -0.05], name: "winch" },
    { geometry: new THREE.BoxGeometry(0.025, 0.12, 0.025), color: "#3a2c1c", position: [0.13, top + 0.46, -0.05], name: "winch" },
    // A loaded ore cart out front.
    { geometry: new THREE.BoxGeometry(0.28, 0.16, 0.2), color: CART, position: [0.34, 0.14 + 0.1, 0.5] },
    { geometry: new THREE.BoxGeometry(0.24, 0.08, 0.16), color: ORE, position: [0.34, 0.14 + 0.2, 0.5] },
    { geometry: new THREE.CylinderGeometry(0.04, 0.04, 0.04, 6), color: "#222", rotation: [Math.PI / 2, 0, 0], position: [0.24, 0.14 + 0.04, 0.42] },
    { geometry: new THREE.CylinderGeometry(0.04, 0.04, 0.04, 6), color: "#222", rotation: [Math.PI / 2, 0, 0], position: [0.44, 0.14 + 0.04, 0.42] },
  ];
}

export const mine = defineModel({
  id: "mine",
  tiles: { w: 1, h: 1 },
  batchId: "standard",
  name: "Mine",
  tags: ["building", "mine", "low-poly", "age-of-mcp", "classic", "animated"],
  summary: "Low-poly mine.",
  description: "A pit-head over a mountain seam that extracts ore on a slow timer.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Winch turning",
      duration: EFFECT_LOOP,
      // The axle sits on the finished headframe: top = 0.14 + 0.62.
      variants: ["complete"],
      tracks: [spin("winch", [0, 0.14 + 0.62 + 0.4, -0.05], { axis: "x", turns: 2 })],
    },
  ],
  stations: [
    { id: "face", position: [0, 0.14, 0.42], facing: Math.PI, description: "Where a miner works the seam." },
  ],
  interactions: [
    { id: "work", label: "Working the seam", station: "face", variants: ["complete"], ...benchWork() },
  ],
});
